import { invalidatePolicyQueries } from '@/utils/policy-cache'
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { trpc, trpcClient } from '@/lib/trpc'

/** Files accepted for a policy upload — matches the import dialog. */
export const POLICY_UPLOAD_ACCEPT = '.pdf,.docx,.md,.html,.htm'

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * Upload a new version of an EXISTING policy (catalog or custom), or the first
 * version of an empty one. Reuses the import pipeline but finalizes against the
 * policy's own slug — so it never re-classifies (no duplicate) and `saveContent`
 * records a new version with the prior one retained in history. Imperative
 * start→poll→finalize→poll via the non-cached client.
 *
 * `uploadingSlug` tracks the in-flight policy so callers can show a per-row /
 * per-action spinner. On success it invalidates the shared policy queries
 * (list + detail editor content) so every open surface refreshes.
 */
export function usePolicyVersionUpload(programId: string | undefined) {
  const queryClient = useQueryClient()
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null)

  const upload = useCallback(
    async (slug: string, file: File) => {
      if (!programId) return
      setUploadingSlug(slug)
      // Persistent progress feedback — parsing a PDF can take several seconds,
      // and on the detail page the triggering menu closes immediately, so the
      // only visible cue is this toast (resolved to success/error below).
      const toastId = toast.loading('Uploading new version…')
      try {
        const base64 = await fileToBase64(file)
        const { extractionId } = await trpcClient.policyImport.startImport.mutate(
          {
            programId,
            extractQuestionnaire: false,
            file: {
              filename: file.name,
              contentType: file.type || 'application/octet-stream',
              base64Data: base64,
              size: file.size,
            },
          },
        )
        const waitForPhase = async (done: (phase: string) => boolean) => {
          for (let i = 0; i < 240; i++) {
            await sleep(500)
            const { phase } =
              await trpcClient.policyImport.getExtractionProgress.query({
                extractionId,
              })
            if (phase === 'failed') throw new Error('Could not read that file')
            if (done(phase)) return
          }
          throw new Error('Upload timed out')
        }
        // Parsed & ready (we ignore the AI suggestion — the slug is known).
        await waitForPhase((p) => p === 'classified' || p === 'complete')
        await trpcClient.policyImport.finalizeImports.mutate({
          programId,
          items: [{ extractionId, slug }],
        })
        await waitForPhase((p) => p === 'complete')
        toast.success('New version uploaded', { id: toastId })
        // Refresh both the list rows and the detail editor content/versions.
        await Promise.all([
          invalidatePolicyQueries(queryClient),
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyVersions.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey(),
          }),
        ])
      } catch (error) {
        toast.error(
          (error as Error).message ?? 'Failed to upload new version',
          { id: toastId },
        )
      } finally {
        setUploadingSlug(null)
      }
    },
    [programId, queryClient],
  )

  return { upload, uploadingSlug }
}
